import * as React from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  Platform,
  PixelRatio,
  Pressable,
  Keyboard,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import ScreenContext from '../ScreenContext';
import BackSubheader from '../elements/BackSubheader';
import KeyboardAvoidingView from '../elements/365KeyboardAvoidingView';
import Styles from '../Styles';
import ActionsFactory from 'src/actions/ActionsFactory';
import NavActions from 'src/actions/NavActions';
import AccountStore from 'src/stores/AccountStore';
import Localized from 'src/constants/AppStrings';
import {alertError, confirm} from '../helpers/AlertHelper';
import {NavigationProp} from '@react-navigation/native';
import Util, {getPreviousRouteName} from 'src/Util';
import FirebaseAnalytic from '../../nativeModules/FirebaseAnalytic';
import {
  fetchRecentCampusesAndLocations,
  swicthCurrentCampusesAndLocations,
} from 'src/redux/slices/campusLocationSlice';
import {connect} from 'react-redux';
import withIsConnected from 'src/components/hoc/withIsConnected';
import {AppDispatch, RootState} from 'src/redux/store';
import AppRoutes from 'src/AppRoutes';
import Settings from 'src/Settings';
import Logger from 'src/logging/Logger';
import AVText from 'src/components/elements/AVText';
import {getDescriber} from './descriptor/DescriptorType';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import WarningDialog from 'src/components/elements/WarningDialog';
import RoundedButton, {ButtonType} from '../elements/RoundedButton';
import GmaApi from 'src/api/GmaApi';
import {Location} from '../../types/serverTypes/Location';
import PlatformApi from 'src/api/PlatformApi';

const LOCATION_CODE_MAX_LENGTH = 12;
const LOCATION_CODE_MIN_LENGTH = 1;

type LocationCodeScreenProps = {
  navigation: NavigationProp<LocationCodeScreen>;
  dispatch: AppDispatch;
  accountId: string;
  isHomeLocationSma: boolean;
  homeOrgId: string;
  isFromHome: boolean;
};

type LocationCodeScreenState = {
  locationCode: string;
  previousRoute: string | null;
  isButtonDisabled?: boolean;
  locationMismatchError: boolean;
};
const {locationCodebgColor, getLinkLocationButton} = getDescriber();

class LocationCodeScreen extends React.Component<
  LocationCodeScreenProps,
  LocationCodeScreenState
> {
  static contextType = ScreenContext;
  declare context: React.ContextType<typeof ScreenContext>;

  constructor(props: LocationCodeScreenProps) {
    super(props);
    this.state = {
      locationCode: '',
      previousRoute: null,
      isButtonDisabled: false,
      locationMismatchError: false,
    };
    this.saveClick = this.saveClick.bind(this);
    this.updateSelectedLocationOrCampus =
      this.updateSelectedLocationOrCampus.bind(this);
    this.isLocationCodeValid = this.isLocationCodeValid.bind(this);
    this.handleLocationCodeChange = this.handleLocationCodeChange.bind(this);
    this.verifyLocationBalanceType = this.verifyLocationBalanceType.bind(this);
    this.closeAccountBalanceMismacthError =
      this.closeAccountBalanceMismacthError.bind(this);
  }

  componentDidMount(): void {
    FirebaseAnalytic.trackEvent('componentDidMount', 'LocationCodeScreen', {
      ...this.props,
      ...this.state,
    });
    const previousRoute = getPreviousRouteName(
      this.props.navigation.getState().routes,
    );
    this.setState({previousRoute});
  }

  async updateSelectedLocationOrCampus(locationId: string) {
    await this.props.dispatch(
      swicthCurrentCampusesAndLocations({
        accountId: this.props.accountId,
        locationId,
      }),
    );
    await this.props.dispatch(
      fetchRecentCampusesAndLocations(this.props.accountId),
    );
    this.props.isFromHome
      ? NavActions.popToTop()
      : NavActions.navigate(AppRoutes.HomeTab);
    if (Platform.OS === 'web') {
      NavActions.pop();
    }
  }

  async verifyLocationBalanceType(linkingLocation: Location): Promise<boolean> {
    const linkingLocationsList = await Util.filterLocationsPerBalanceType(
      this.props.isHomeLocationSma,
      this.props.homeOrgId,
      [linkingLocation],
    );
    if (linkingLocationsList.unSupportedLocations.length > 0) {
      return Promise.resolve(false);
    } else if (linkingLocationsList.supportedLocations.length > 0) {
      return Promise.resolve(true);
    }
  }

  closeAccountBalanceMismacthError() {
    this.setState({locationMismatchError: false, isButtonDisabled: false});
  }

  async saveClick() {
    FirebaseAnalytic.trackEvent('saveClick', 'LocationCodeScreen', {
      ...this.props,
      ...this.state,
    });
    this.setState({isButtonDisabled: true});
    if (
      this.state.locationCode &&
      this.state.locationCode.length >= LOCATION_CODE_MIN_LENGTH
    ) {
      const locationByBeacon = await PlatformApi.locationSearchByBeacon(
        this.state.locationCode,
      );

      if (!locationByBeacon || !locationByBeacon?.locationId) {
        this.handleInvalidLocationCode();
        return;
      }

      const location = await PlatformApi.fetchLocation(locationByBeacon.locationId);
      if (!location || !location?.name) {
        this.handleInvalidLocationCode();
        return;
      }
      FirebaseAnalytic.trackEvent(
        'saveClick getLocation',
        'LocationCodeScreen',
        {
          ...this.props,
          ...this.state,
          response: locationByBeacon,
          homeLocation: JSON.stringify(location),
        },
      );

      const isBalanceSupported = await this.verifyLocationBalanceType(location);
      if (!isBalanceSupported) {
        Keyboard.dismiss();
        this.setState({locationMismatchError: true});
        return;
      }

      if (location.onlineOrderConfig) {
        this.onlineOrderConfigLocationConfirm(location);
      } else {
        this.defaultLocationConfirm(location);
      }
    } else {
      this.handleInvalidLocationCode();
    }
  }

  private handleInvalidLocationCode(): void {
    alertError(
      Localized.Errors.dont_know_location_code,
      null,
      undefined,
      Localized.Errors.invalid_location_code,
    );
    this.setState({isButtonDisabled: false});
  }

  private async setHomeLocation() {
    // Make the location the user's home location
    const account = await GmaApi.getAccountByBroadcastId(
      this.state.locationCode,
      AccountStore.getAccountId(),
    );
    if (account && account.locationId) {
      await ActionsFactory.getAccountActions().getLocation(account.locationId);
    }
  }

  private defaultLocationConfirm(location: Location) {
    confirm(
      this.getLocationAddressDisplay(location),
      async () => {
        const homeLocation = AccountStore.getLocationId();
        if (!homeLocation) {
          await this.getSubsidyBalanceIntoNewAccount();
          await this.setHomeLocation();
        }
        await this.updateSelectedLocationOrCampus(location.locationId);
      },
      undefined,
      location.name,
      Localized.Labels.back,
      Localized.Buttons.confirm,
    );
    this.setState({isButtonDisabled: false});
  }

  private onlineOrderConfigLocationConfirm(
     location: Location,
  ) {
    confirm(
      this.getLocationAddressDisplay(location),
      async () => {
        const homeLocation = AccountStore.getLocationId();
        if (!homeLocation) {
          await this.getSubsidyBalanceIntoNewAccount();
          await this.setHomeLocation();
        }

        ActionsFactory.getAccountActions().addLocationLink(
          AccountStore.getAccountId(),
          location.locationId,
        );
        this.updateSelectedLocationOrCampus(location.locationId);
      },
      undefined,
      location.name,
      Localized.Labels.back,
      Localized.Buttons.confirm,
    );
    this.setState({isButtonDisabled: false});
  }

  private async getSubsidyBalanceIntoNewAccount(): Promise<void> {
    // this is a working around to get the subsidy balance into a new account without the API changes.
    const res = await GmaApi.getAccountByBroadcastId(
      this.state.locationCode,
      AccountStore.getAccountId(),
    );
    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'GetLocationByBeacon',
      JSON.stringify({
        accountId: AccountStore.getAccountId(),
        locationCode: this.state.locationCode,
      }),
      JSON.stringify(res),
    );
    const deviceLookUpResponse =
      await ActionsFactory.getAccountActions().deviceLookUp(
        res.locationId,
        AccountStore.getAccountId(),
      );
    Logger.Log.LogAPIEvent(
      'AccountAPI',
      'DeviceLookUp',
      JSON.stringify({
        accountId: AccountStore.getAccountId(),
        locationId: res.locationId,
      }),
      JSON.stringify(deviceLookUpResponse),
    );
  }

  getLocationAddressDisplay(location: Location) {
    FirebaseAnalytic.trackEvent(
      'getLocationAddressDisplay',
      'LocationCodeScreen',
      {
        ...this.props,
        ...this.state,
        location,
      },
    );
    if (location.city && location.state) {
      return `${location.city}, ${location.state}`;
    } else if (location.city) {
      return location.city;
    } else if (location.state) {
      return location.state;
    }

    return '';
  }

  isLocationCodeValid = () => {
    const locationCode = this.state.locationCode.trim();
    if (locationCode && locationCode.length > 0) {
      return false;
    } else {
      return true;
    }
  };

  handleLocationCodeChange(text) {
    this.setState({locationCode: text});
  }

  render() {
    const locationNote = `${Localized.Labels.please_enter_location_code}\n\n${Localized.Labels.if_you_do_not_have_a_code}`;
    return (
      <BackSubheader
        previousRoute={this.state.previousRoute}
        accessibilityLabel={'Back arrow'}
        accessibilityHint={`Press to navigate back to the ${this.state.previousRoute} screen`}
        title={Localized.Labels.link_new_location}
      >
        {this.state.locationMismatchError && (
          <WarningDialog
            isVisible={this.state.locationMismatchError}
            buttonAction={this.closeAccountBalanceMismacthError}
            closeAction={this.closeAccountBalanceMismacthError}
          />
        )}

        <KeyboardAvoidingView behavior="height" insideTab>
          <ScrollView>
            <View
              style={[
                styles.content,
                {
                  backgroundColor:
                    Settings.isRefiveAnd365Pay() && Platform.OS !== 'web'
                      ? Styles.tabBarBackgroundColor
                      : Settings.isCanteen() && Platform.OS !== 'web'
                      ? Styles.Colors.Canteen.lightGray
                      : Styles.white,
                },
              ]}
            >
              <Text
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                style={
                  getDescriber().baseAccountContainerDescriptor().headerText
                }
                accessible={true}
                aria-label="Enter the location code, text"
                accessibilityLabel="Enter the location code"
                nativeID="Enter the location code"
              >
                {Localized.Labels.enter_your_location_code}
              </Text>
              {getDescriber().getLocationCodeText(
                this.state,
                LOCATION_CODE_MAX_LENGTH,
                this.handleLocationCodeChange,
                locationNote,
              )}
            </View>
            {PixelRatio.getFontScale() > 1 && (
              <View style={{height: Styles.Heights.headerHeight}} />
            )}
          </ScrollView>
          {Settings.buildType === 'default' ||
          Settings.buildType === 'refive' ? (
            <Pressable
              style={[
                styles.saveBtn,
                !this.isLocationCodeValid()
                  ? {
                      backgroundColor: locationCodebgColor(),
                      borderTopColor: locationCodebgColor(),
                      borderBottomColor: locationCodebgColor(),
                      borderLeftColor: locationCodebgColor(),
                      borderRightColor: locationCodebgColor(),
                    }
                  : styles.validLocationStyle,
              ]}
              onPress={this.saveClick}
              role="button"
              testID="qa_btn_Save"
              accessible={true}
              accessibilityLabel={Localized.Buttons.save}
              nativeID={'Btn-' + Localized.Buttons.save}
              accessibilityRole="button"
              aria-label={Localized.Buttons.save}
              disabled={
                this.isLocationCodeValid() || this.state.isButtonDisabled
              }
            >
              <AVText
                style={[
                  styles.saveButtonTextdefault,
                  getSnackDescriber().snackDetailsBtnFontSTyle(),
                ]}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
                testID="save"
              >
                {Localized.Buttons.save}
              </AVText>
            </Pressable>
          ) : (
            <RoundedButton
              disabled={
                this.isLocationCodeValid() || this.state.isButtonDisabled
              }
              accessible={true}
              accessibilityLabel={Localized.Buttons.save}
              accessibilityRole="button"
              role="button"
              aria-label={Localized.Buttons.save}
              buttonType={ButtonType.normal}
              text={Localized.Buttons.save}
              onPress={this.saveClick}
              textStyle={getDescriber().locationCodeSaveButtonTextStyle()}
              containerStyle={styles.containerStyle}
              testID="qa_btn_Save"
            />
          )}
        </KeyboardAvoidingView>
      </BackSubheader>
    );
  }
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingHorizontal: !Settings.isNewUI()
      ? Styles.Spacing.m3
      : Styles.Spacing.m3 + Styles.Spacing.m2,
    ...(Platform.OS === 'web' && {
      alignSelf: 'center',
    }),
  },

  saveBtn: {
    borderRadius: Styles.Spacing.m4,
    borderWidth: Platform.OS === 'android' ? 1.2 : 1,
    paddingVertical: Styles.Spacing.m2 + 2,
    paddingHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginHorizontal: Styles.Spacing.m3,
    bottom: Styles.Spacing.m3,
    right: Styles.Spacing.m3,
    position: 'absolute',
    overflow: 'hidden',
    minWidth: Styles.MinTouchableArea,
  },
  saveButtonTextdefault: {
    fontWeight: Platform.OS === 'android' ? 'bold' : '700',
    fontFamily: Styles.FontFamily.aeonikRegular,
    fontSize: Styles.Fonts.f1,
    color: Styles.Colors.PayNew.white01,
  },

  saveButtonText: {
    fontSize: Styles.Fonts.f7,
    fontWeight: '700',
    fontFamily: Styles.FontFamily.figtreeRegular,
  },
  containerStyle: {
    alignItems: 'center',
    marginHorizontal: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m3,
  },
  validLocationStyle: {
    backgroundColor: Styles.Colors.PayNew.darkHuesBase04,
    borderTopColor: Styles.Colors.PayNew.darkHuesBase04,
    borderBottomColor: Styles.Colors.PayNew.darkHuesBase04,
    borderLeftColor: Styles.Colors.PayNew.darkHuesBase04,
    borderRightColor: Styles.Colors.PayNew.darkHuesBase04,
  },
});

const ConnectedLocationCodeScreen = connect(
  (state: RootState) => ({
    accountId: state.account.account.id,
    isHomeLocationSma: state.account.isHomeLocationSma,
    homeOrgId: state.account.account.orgId,
  }),
  (dispatch: AppDispatch) => ({
    dispatch,
  }),
)(LocationCodeScreen);

export default withForwardedNavigationParams()(
  withIsConnected(ConnectedLocationCodeScreen),
);
